import { theme as antdTheme, type ThemeConfig } from 'antd'

import { palettes } from './palette'
import type { ThemeMode } from './palette'
import { darkThemeTokens, themeTokens } from './themeTokens'

export type { ThemeMode } from './palette'

// The per-mode AntD theme — ported by value from ebs-platform-web
// (`packages/shared/src/theme/useTheme.ts`), the design-system source of truth.
// Colours come from `palette.ts`, globals from `themeTokens.ts`; change them in
// ebs first, then mirror them here.

const buildTheme = (mode: ThemeMode): ThemeConfig => {
  const palette = palettes[mode]
  const tokens = mode === 'dark' ? darkThemeTokens : themeTokens

  return {
    ...(mode === 'dark' && { algorithm: antdTheme.darkAlgorithm }),
    // CSS-variable mode, so component overrides can reference live tokens via
    // `var(--ant-color-*)`. account-ui keeps its own key and unhashed class
    // names (ebs uses key 'skalx' on <html>); only the wiring differs.
    cssVar: { key: 'antd' },
    hashed: false,
    token: tokens,
    components: {
      Layout: {
        headerBg: palette.container,
        headerHeight: 48,
        headerPadding: '0 10px',
        siderBg: palette.container,
        bodyBg: palette.layout,
        footerBg: palette.layout
      },
      Menu: {
        itemBg: 'transparent',
        itemSelectedBg: palette.selected,
        itemSelectedColor: palette.text,
        itemHoverBg: palette.hover,
        itemHoverColor: palette.text,
        itemColor: palette.menuText,
        itemHeight: 30,
        itemBorderRadius: 6,
        itemMarginInline: 8,
        itemMarginBlock: 2,
        itemPaddingInline: 10,
        iconSize: 15,
        collapsedIconSize: 17,
        subMenuItemBg: 'transparent',
        iconMarginInlineEnd: 10,
        groupTitleFontSize: 11,
        groupTitleColor: palette.textTertiary
      },
      Button: {
        // controlHeight* inherit the global compact scale (28 / 24 / 32).
        paddingInline: 10,
        paddingInlineSM: 8,
        paddingInlineLG: 14,
        fontWeight: 500,
        primaryShadow: 'none',
        defaultShadow: 'none',
        dangerShadow: 'none',
        defaultBg: palette.container,
        defaultBorderColor: palette.border,
        defaultColor: palette.text,
        defaultHoverBg: palette.container,
        // default-button hover (color + border) is left to AntD — it derives
        // it from colorPrimary, so it follows the seed (and the org-settings
        // cascade) instead of being pinned to teal.
        // SM/LG radius are intentionally flattened to 6 (global SM=4 / LG=8);
        // base radius 6 is inherited from the global token.
        borderRadiusSM: 6,
        borderRadiusLG: 6,
        textHoverBg: palette.hover,
        primaryColor: 'var(--brand-on-fill)',
        dangerColor: palette.textOnPrimary
      },
      Input: {
        // inputFontSize* are tuned (with paddingBlock) to hit exact 24/28/32px
        // heights — see docs; not pure global duplicates. colorBorder, hover/
        // active border (colorPrimaryBorderHover / colorPrimary) all inherit and
        // follow the seed. Only the 3px focus ring is custom — it tracks the seed
        // via --brand-teal-soft (a color-mix of --brand-teal in tokens.css).
        inputFontSize: 13,
        inputFontSizeSM: 12,
        inputFontSizeLG: 14,
        activeShadow: '0 0 0 3px var(--brand-teal-soft)'
      },
      InputNumber: {
        // controlHeight* + border colors inherit the global/derived tokens.
        activeShadow: '0 0 0 3px var(--brand-teal-soft)'
      },
      Select: {
        // controlHeight*, borderRadius, colorBorder and hover/active border all
        // inherit and follow the seed. The focus outline tracks the seed via
        // --brand-teal-soft.
        optionSelectedBg: palette.selected,
        optionSelectedColor: palette.text,
        optionSelectedFontWeight: 400,
        optionActiveBg: palette.hover,
        optionPadding: '5px 10px',
        optionHeight: 28,
        activeOutlineColor: 'var(--brand-teal-soft)'
      },
      Cascader: {
        optionSelectedFontWeight: 400
      },
      Mentions: {
        fontWeightStrong: 400
      },
      DatePicker: {
        // controlHeight*, colorBorder and hover/active border inherit and follow
        // the seed.
        activeShadow: '0 0 0 3px var(--brand-teal-soft)',
        cellActiveWithRangeBg: palette.selected,
        colorTextLightSolid: palette.textOnPrimary
      },
      Table: {
        headerBg: palette.subtle,
        headerColor: palette.textSecondary,
        headerSplitColor: 'transparent',
        rowHoverBg: palette.rowHover,
        rowSelectedBg: palette.rowSelected,
        rowSelectedHoverBg: palette.rowSelectedHover,
        cellPaddingBlock: 9,
        cellPaddingInline: 12,
        cellPaddingBlockMD: 8,
        cellPaddingBlockSM: 6,
        cellPaddingInlineSM: 12,
        borderColor: palette.split,
        footerBg: tokens.colorBgContainer,
        footerColor: tokens.colorText,
        headerBorderRadius: 8,
        fontWeightStrong: 600,
        headerSortActiveBg: palette.sortedColumn,
        headerSortHoverBg: palette.sortedColumn,
        bodySortBg: palette.sortedColumn
      },
      Card: {
        headerBg: 'transparent',
        headerFontSize: 14,
        headerHeight: 44,
        headerHeightSM: 36,
        paddingLG: 12,
        padding: 10,
        borderRadiusLG: 8,
        boxShadowTertiary: tokens.boxShadowTertiary,
        colorBorderSecondary: palette.borderSecondary
      },
      Tabs: {
        cardBg: palette.hover,
        itemColor: palette.textTertiary,
        itemHoverColor: palette.text,
        titleFontSize: 13,
        horizontalItemPadding: '0px 4px 4px 0px',
        horizontalItemGutter: 16,
        horizontalMargin: '0 0 12px 0',
        paddingContentVertical: 12
        // inkBarColor inherits colorPrimary (the AntD default) → follows the seed.
      },
      Tag: {
        // Default (no-color) Tag — light cool-grey pill with warm-slate text.
        // `colorBorder: 'transparent'` keeps the default Tag borderless (filled
        // chip). Preset-colored Tags ignore this and pick their own
        // `${color}3` border via AntD's outlined variant (configured in the
        // org ConfigProvider), so status pills get a defined edge.
        defaultBg: palette.tag,
        colorBgSolid: palette.tag,
        defaultColor: palette.textSecondary,
        // borderRadiusSM (4) inherits the global token.
        colorBorder: 'transparent',
        fontSizeSM: 11,
        lineHeightSM: 1.4,
        colorTextLightSolid: palette.textOnPrimary
      },
      Badge: {
        indicatorHeight: 16,
        textFontSize: 10,
        textFontWeight: 600,
        // CSS var so the dot follows the org-merged `colorError` rather than
        // freezing the literal hex at module-import time.
        colorBgContainer: 'var(--ant-color-error)',
        dotSize: 6,
        colorError: palette.badgeFill
      },
      Avatar: {
        groupBorderColor: palette.container,
        colorTextPlaceholder: palette.container,
        borderRadius: 6
      },
      Tooltip: {
        colorBgSpotlight: palette.spotlight,
        borderRadius: 5,
        fontSize: 12
      },
      Modal: {
        borderRadiusLG: 10,
        titleFontSize: 16,
        paddingLG: 20,
        headerBg: 'transparent'
      },
      Drawer: {
        paddingLG: 20
      },
      Dropdown: {
        // borderRadiusLG (8) and fontSize (13) inherit the global tokens.
        paddingBlock: 6,
        controlItemBgHover: palette.hover,
        controlItemBgActive: palette.itemActive,
        controlItemBgActiveHover: palette.itemActiveHover,
        ...(mode === 'light' && { colorError: palette.badgeFill })
      },
      Form: {
        labelFontSize: 12,
        labelColor: palette.textSecondary,
        labelHeight: 18,
        // CSS var so the asterisk follows the org-merged `colorError` rather
        // than freezing the literal hex at module-import time.
        labelRequiredMarkColor: 'var(--ant-color-error)',
        itemMarginBottom: 10,
        verticalLabelPadding: '0 0 2px',
        verticalLabelMargin: 0
      },
      Pagination: {
        itemSize: 28,
        itemSizeSM: 24,
        // controlHeight inherits the global token (28).
        // SkalX pagination uses a filled-pill for the active page (AntD
        // defaults to outlined). CSS vars so the fill follows the live
        // primary instead of freezing the import-time teal hex.
        itemActiveBg: 'var(--brand-fill)',
        itemActiveBgDisabled: 'var(--ant-color-primary)',
        itemActiveColor: 'var(--brand-on-fill)',
        itemActiveColorHover: 'var(--brand-on-fill)',
        itemActiveColorDisabled: tokens.colorBgContainer,
        itemInputBg: tokens.colorBgContainer
      },
      Statistic: {
        titleFontSize: 12,
        contentFontSize: 22
      },
      Steps: {
        titleLineHeight: 1.4,
        iconSize: 24,
        iconFontSize: 13,
        colorTextLightSolid: palette.textOnPrimary
      },
      Tree: {
        directoryNodeSelectedColor: palette.textOnPrimary
      },
      Tour: {
        colorTextLightSolid: palette.textOnPrimary
      },
      Divider: {
        colorSplit: palette.split,
        marginLG: 14
      },
      Alert: {
        // borderRadiusLG (8) and fontSize (13) inherit the global tokens.
        defaultPadding: '8px 12px',
        withDescriptionPadding: '12px 14px',
        colorInfoBg: palette.infoBg,
        colorInfoBorder: palette.infoBorder,
        colorSuccessBg: palette.successBg,
        colorSuccessBorder: palette.successBorder,
        colorWarningBg: palette.warningBg,
        colorWarningBorder: palette.warningBorder,
        colorErrorBg: palette.errorBg,
        colorErrorBorder: palette.errorBorder
      },
      Notification: {
        borderRadiusLG: 10
      },
      Message: {
        contentBg: palette.container,
        contentPadding: '8px 12px',
        borderRadiusLG: 6,
        colorText: palette.text
      },
      Segmented: {
        // borderRadius (6) inherits the global token.
        itemSelectedBg: palette.segmentSelected,
        itemSelectedColor: palette.text,
        itemColor: palette.textTertiary,
        itemHoverColor: palette.text,
        trackBg: palette.track,
        trackPadding: 2,
        itemActiveBg: palette.segmentSelected
      },
      Breadcrumb: {
        itemColor: palette.textTertiary,
        lastItemColor: palette.text,
        separatorColor: palette.textQuaternary,
        fontSize: 13
      },
      Empty: {
        colorTextDisabled: palette.textQuaternary
      },
      Progress: {
        remainingColor: palette.split,
        circleTextFontSize: '14px'
      },
      Checkbox: {
        controlInteractiveSize: 15,
        borderRadiusSM: 3
      },
      Switch: {
        handleSize: 14,
        trackHeight: 18,
        trackMinWidth: 32
      },
      Timeline: {
        itemPaddingBottom: 14,
        tailColor: palette.borderSecondary,
        dotBorderWidth: 2
      },
      List: {
        itemPadding: '10px 0',
        itemPaddingSM: '6px 0'
      },
      Descriptions: {
        labelBg: 'transparent',
        labelColor: palette.textTertiary,
        contentColor: palette.text,
        colonMarginRight: 6,
        colonMarginLeft: 0,
        padding: 12,
        paddingSM: 4,
        itemPaddingBottom: 6,
        itemPaddingEnd: 6,
        titleMarginBottom: 8,
        extraColor: palette.textTertiary
      },
      Slider: {
        // Full-teal track/handle (brighter than AntD's default colorPrimaryBorder)
        // pinned to the derived primary so they follow the seed.
        handleColor: 'var(--ant-color-primary)',
        trackBg: 'var(--ant-color-primary)'
      },
      Radio: {
        buttonCheckedBg: palette.choiceSelected,
        buttonColor: palette.textSecondary,
        buttonSolidCheckedBg: 'var(--brand-fill)',
        buttonSolidCheckedHoverBg: 'var(--brand-fill-hover)',
        buttonSolidCheckedActiveBg: 'var(--brand-fill-press)',
        buttonSolidCheckedColor: 'var(--brand-on-fill)'
      },
      Affix: {
        zIndexBase: 5
      },
      Anchor: {
        linkPaddingBlock: 4
      },
      Skeleton: {
        gradientFromColor: palette.skeletonFrom,
        gradientToColor: palette.skeletonTo
      },
      Typography: {
        titleMarginTop: 0,
        titleMarginBottom: 0
      }
    }
  }
}

const themes: Partial<Record<ThemeMode, ThemeConfig>> = {}

export const getTheme = (mode: ThemeMode): ThemeConfig =>
  (themes[mode] ??= buildTheme(mode))

// Backwards-compat default export (light) for any non-provider consumer.
export const theme: ThemeConfig = getTheme('light')
